import { brand } from "../../config/theme";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import { useUserStore } from "../../store/userStore";
import { useSessionCheck } from "../../hooks/useSessionCheck";
import { setLoggingOut } from "../../utils/tokenUtils";
import { logoutUser } from "../../pages/login/functions";

import { Header } from "../../components/base/Header";
import { AppsSidebar, useFilteredCategories } from "../../components/base/AppsSidebar";
import { AppSidebarMenu } from "../../components/base/AppSidebarMenu";
import { useTheme } from "../../context/ThemeContext";
import type { AppItem } from "../../components/base/AppsSidebar";

import { MENU, MANTENIMIENTO_BASE } from "./config";
import { ROLE_LABEL_KEY } from "./roles";
import { useMaintenanceCapabilities } from "./hooks/useMaintenanceCapabilities";
import { useAccess } from "../../hooks/useAccess";

// Single shell for the Mantenimiento a Vehículos mini-app. Renders the flat
// section sub-menu (derived from MENU), the apps sidebar, and the matched
// section via <Outlet/>. Mirrors NuvexAccessLayout / SGLayout.
export default function MantenimientoLayout() {
  const { t } = useTranslation("common");
  const { canAccessSectionOfApp } = useAccess();
  const { role } = useMaintenanceCapabilities();
  const { themeMode, setTheme } = useTheme();
  const filteredCategories = useFilteredCategories();
  const user = useUserStore((state) => state.user);
  const hasRedirected = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const { isExpiringSoon, timeRemainingFormatted, isExpired, isAuthenticated, isInitialized } =
    useSessionCheck({ warningThreshold: 60 * 60 * 1000, autoRefresh: true });

  const [isAppsSidebarOpen, setIsAppsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const openAppsSidebar = () => setIsAppsSidebarOpen(true);
  const closeAppsSidebar = () => setIsAppsSidebarOpen(false);

  const handleAppSelect = (app: AppItem) => {
    if (app.isComingSoon) {
      Swal.fire({
        title: t("appsSidebar.comingSoon"),
        text: t("appsSidebar.comingSoonText", { name: app.name }),
        icon: "info",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    if (app.isLocked) {
      Swal.fire({
        title: t("appsSidebar.premiumFeature"),
        text: t("appsSidebar.premiumRequired", { name: app.name }),
        icon: "warning",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    if (app.route) {
      closeAppsSidebar();
      navigate(app.route);
    } else if (app.externalUrl) {
      window.open(app.externalUrl, "_blank");
    }
  };

  const menuItems = MENU
    .filter((item) => canAccessSectionOfApp("mantenimiento-vehiculos", item.id))
    .map((item) => ({
    id: item.id,
    label: t(item.labelKey),
    icon: item.icon,
    path: `${MANTENIMIENTO_BASE}/${item.id}`,
  }));

  useEffect(() => {
    if (isInitialized && (isExpired || !isAuthenticated) && !hasRedirected.current) {
      hasRedirected.current = true;
      setLoggingOut(false);
      navigate("/");
    }
  }, [isExpired, isAuthenticated, isInitialized, navigate]);

  useEffect(() => {
    if (!user && location.pathname !== "/splash") {
      localStorage.setItem("lastRoute", location.pathname);
      navigate("/splash");
    }
  }, [user, location.pathname, navigate]);

  return (
    <div className="h-full bg-app">
      {isExpiringSoon && (
        <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-2">
          <i className="ri-alarm-warning-line"></i>
          <span>{t("sessionBanner.expiring", { time: timeRemainingFormatted })}</span>
        </div>
      )}

      <Header
        title={t("mantenimiento.appName")}
        subtitle={t("mantenimiento.appSubtitle")}
        onOpenAppsSidebar={openAppsSidebar}
        isAppsSidebarOpen={isAppsSidebarOpen}
        />

      <div className="flex h-full">
        <AppSidebarMenu
          menuItems={menuItems}
          isMobileMenuOpen={false}
          onCloseMobileMenu={() => {}}
          onNavigate={(path) => navigate(path)}
          isActiveRoute={(itemId) =>
            location.pathname === `${MANTENIMIENTO_BASE}/${itemId}`
          }
          footerStats={{
            label: t(ROLE_LABEL_KEY[role]),
            items: [
              { label: t("mantenimiento.menu.ordenes"), value: MENU.length },
            ],
          }}
        />

        <div className="flex-1 overflow-hidden flex justify-center w-full">
          <Outlet />
        </div>
      </div>

      <AppsSidebar
        isOpen={isAppsSidebarOpen}
        onClose={closeAppsSidebar}
        categories={filteredCategories}
        selectedAppId="mantenimiento-vehiculos"
        onAppSelect={handleAppSelect}
        onStoreClick={() => navigate("/store")}
        currentTheme={themeMode}
        onThemeChange={setTheme}
        userInfo={
          user
            ? {
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                username: user.username,
              }
            : undefined
        }
        onProfileClick={() => {
          closeAppsSidebar();
          navigate("/profile");
        }}
        onLogout={async () => {
          setIsLoggingOut(true);
          await logoutUser();
          setIsLoggingOut(false);
        }}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
}
